import type { ITask as ICountryTask } from '@bridebook/models/source/models/Countries/Tasks.types';
import type { IDatePicker, IWedding } from '@bridebook/models/source/models/Weddings.types';
import type { ITask as IWeddingTask } from '@bridebook/models/source/models/Weddings/Tasks.types';
import gazetteer, { type CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { getSplitTasks } from '@bridebook/web/lib/checklist/Tasks.utils';
import { getTasksExactDate } from '@bridebook/web/lib/checklist/utils';
import type { PlanningBlueprint, ResolvedChecklist, ResolvedTask } from './types';

// A wedding task as the resolver reads it: the fields shared by the stored record and the
// serialised store task, so either one works as is.
export type StoredTask = Pick<
  IWeddingTask,
  'id' | 'group' | 'name' | 'period' | 'order' | 'done' | 'custom'
>;

export type ResolveChecklistInput = {
  blueprint: PlanningBlueprint | null;
  // The couple's own tasks, from Firestore.
  weddingTasks: Record<string, StoredTask> | undefined;
  // False while the checklist is still loading.
  checklistLoaded: boolean;
  date: IDatePicker | undefined;
  country: string | null | undefined;
  initializedAt: number | null | undefined;
  roles: IWedding['roles'] | undefined;
  isSameSexRoles: boolean;
  // When the projected timeline starts. Without it the projection starts from "now", and
  // periods shift as weeks pass, so the coach would contradict its earlier advice.
  projectionAnchorAt: number | undefined;
};

/**
 * The couple's checklist for the coach: their own when they have one, otherwise projected
 * from the country template and their date. A projection is never saved.
 *
 * Null while still loading, so a couple with a real checklist never sees a projection.
 */
export const resolveChecklist = (input: ResolveChecklistInput): ResolvedChecklist | null => {
  const { blueprint, weddingTasks, checklistLoaded, initializedAt } = input;
  if (!(blueprint && checklistLoaded)) {
    return null;
  }

  if (initializedAt) {
    const tasks = Object.values(weddingTasks ?? {}).map((task) =>
      resolveStoredTask(task, blueprint),
    );
    // A custom main task heads its own group, so its name names the group.
    const names = new Map(tasks.map((task) => [task.id, task.name]));
    return {
      tasks: tasks.map((task) => ({
        ...task,
        groupName: blueprint.groups[task.group]?.name ?? names.get(task.group),
      })),
      projected: false,
      initializedAt,
      approximateDate: false,
    };
  }

  return projectChecklist(input, blueprint);
};

const resolveStoredTask = (task: StoredTask, blueprint: PlanningBlueprint): ResolvedTask => {
  const template = blueprint.tasks[task.id];
  return {
    id: task.id,
    group: task.group ?? template?.group ?? '',
    // Stored wedding tasks have no names for template tasks, so use the template's name,
    // as the checklist page does. A custom task has its own name.
    name: template?.name || task.name || task.id,
    period: task.period ?? template?.period ?? '-P',
    order: task.order ?? template?.order ?? 0,
    done: Boolean(task.done),
    custom: Boolean(task.custom),
  };
};

// Runs the app's own checklist setup over the template, so a projected timeline matches
// what the couple would get by starting their checklist.
const projectChecklist = (
  input: ResolveChecklistInput,
  blueprint: PlanningBlueprint,
): ResolvedChecklist | null => {
  const { date, country, roles, isSameSexRoles, projectionAnchorAt } = input;
  if (!date || !country) {
    return null;
  }

  const market = gazetteer.getMarketByCountry(country as CountryCodes);
  const exactDate = getTasksExactDate(date, market);
  if (!Number.isFinite(exactDate.getTime())) {
    return null;
  }

  const template: Record<string, ICountryTask> = {};
  for (const task of Object.values(blueprint.tasks)) {
    template[task.id] = {
      id: task.id,
      group: task.group,
      name: task.name,
      period: task.period,
      order: task.order,
      active: true,
      ...(task.roles ? { roles: task.roles as ICountryTask['roles'] } : {}),
    };
  }

  const split = getSplitTasks(template, exactDate, projectionAnchorAt, roles, isSameSexRoles);

  return {
    // The split output has no template names, so add them back, or every task shows as an id.
    tasks: split.map((task) => ({
      id: task.id,
      group: task.group ?? blueprint.tasks[task.id]?.group ?? '',
      name: blueprint.tasks[task.id]?.name ?? task.id,
      period: task.period ?? '-P',
      order: task.order ?? 0,
      done: false,
    })),
    projected: true,
    initializedAt: null,
    approximateDate: date.y == null,
  };
};
